import { useState } from 'react'
import MFAccordion from '../../../../../../../components/ui-components/MFAccordion'
import RenderHTML from '../../../../../../../components/ui-components/RenderHTML'
import { PrimaryButton } from '../../../../../../../components/execution/PrimaryButton'
import { EventNames } from '../../../../../../../types/misc.types'
import { lightImpactVibrate, openEliteLandingPage, openPrimeLandingPage, trackEvent } from '../../../../../../../utils/appBridge'
import { PlansScreen, PlansTimerEventIds } from '../../../../analytics/plansEvents'
import { useAppMetaContent } from '../../../../../../../state-management/providers/AppMetaContentProvider'
import { PlanEducationIcon, type PlanEducationIconId } from './planEducationIcons'
import type { TimerColoredText } from './timerCountdownChrome'

export function TimerDelayCard({
  title,
  body,
  shadowClassName
}: {
  title: TimerColoredText
  body: TimerColoredText
  shadowClassName: string
}) {
  return (
    <div className={`mt-4 rounded-[20px] bg-white px-4 py-4 ${shadowClassName}`}>
      <div className={`text-[16px] font-[500] leading-[140%] ${title.className}`}>{title.text}</div>
      <div className={`pt-2 text-[14px] font-[400] leading-[150%] ${body.className}`}>{body.text}</div>
    </div>
  )
}

export function TimerNotifyBanner({
  notify,
  shadowClassName
}: {
  notify: TimerColoredText
  shadowClassName: string
}) {
  return (
    <div
      className={`mt-4 flex items-center gap-2 rounded-[20px] bg-white px-4 py-4 text-[14px] font-[400] leading-[150%] ${notify.className} ${shadowClassName}`}
    >
      <NotifyBellIcon />
      <span>{notify.text}</span>
    </div>
  )
}

export function TimerUnlockCta({
  prompt,
  buttonLabel,
  trustedByClassName,
  analyticsScreen = PlansScreen.LumpsumAllocationTimer,
  landingPage = 'elite'
}: {
  prompt: TimerColoredText
  buttonLabel: string
  trustedByClassName: string
  analyticsScreen?: string
  landingPage?: 'elite' | 'prime'
}) {
  const { getEliteMembersDisplay } = useAppMetaContent()

  return (
    <div className="mt-6">
      <TimerStackedCta
        prompt={prompt.text}
        buttonLabel={buttonLabel}
        promptClassName={prompt.className}
        onClick={() => {
          lightImpactVibrate()
          trackEvent({
            eventName: EventNames.ButtonTap,
            payload: {
              ScreenName: analyticsScreen,
              eventId: PlansTimerEventIds.UnlockElite,
              buttonLabel
            }
          })
          if (landingPage === 'prime') {
            openPrimeLandingPage()
            return
          }
          openEliteLandingPage()
        }}
      />
      <div className={`text-[13px] pt-2 font-[400] text-center ${trustedByClassName}`}>
        Trusted by {getEliteMembersDisplay('short')} Elite members
      </div>
    </div>
  )
}

export type StrategyCtaVariant = 'find' | 'complete'

export function TimerStrategyCta({
  promptClassName,
  onFindStrategy,
  variant = 'find',
  analyticsScreen = PlansScreen.LumpsumAllocationTimer
}: {
  promptClassName: string
  onFindStrategy: () => void
  variant?: StrategyCtaVariant
  analyticsScreen?: string
}) {
  const isComplete = variant === 'complete'

  return (
    <div className="mt-7">
      <TimerStackedCta
        prompt={
          isComplete
            ? 'Takes just a few mins'
            : 'Takes just 5 mins'
        }
        buttonLabel={isComplete ? 'Continue assessment' : 'Find your strategy'}
        promptClassName={promptClassName}
        onClick={() => {
          lightImpactVibrate()
          trackEvent({
            eventName: EventNames.ButtonTap,
            payload: {
              ScreenName: analyticsScreen,
              eventId: PlansTimerEventIds.StrategyCta,
              buttonLabel: isComplete ? 'Continue assessment' : 'Find your strategy'
            }
          })
          onFindStrategy()
        }}
      />
    </div>
  )
}

export function TimerEducationAccordions({
  rows,
  iconColor,
  analyticsScreen = PlansScreen.LumpsumAllocationTimer
}: {
  rows: readonly {
    iconId: PlanEducationIconId
    title: string
    caption: string
    content: string
  }[]
  iconColor: string
  analyticsScreen?: string
}) {
  const [openAccordions, setOpenAccordions] = useState<Record<string, boolean>>(() =>
    rows.reduce(
      (state, row) => {
        state[row.title] = false
        return state
      },
      {} as Record<string, boolean>
    )
  )

  return (
    <div className="flex flex-col overflow-hidden rounded-[12px] border border-[#F0F3F5]">
      {rows.map(({ title, iconId, caption, content }) => (
        <MFAccordion
          key={title}
          title={title}
          accessibleName={`${title}, ${caption}`}
          isExpanded={openAccordions[title] ?? false}
          iconComponent={<PlanEducationIcon iconId={iconId} color={iconColor} className="h-6 w-6" />}
          iconClassName="!h-6 !w-6 !mt-2"
          accordionClassName="border-b border-[#F0F3F5]"
          middleContent={
            <div className="flex flex-col">
              <div className="text-[14px] font-[500] text-[#232323]">{title}</div>
              <div className="text-[13px] font-[400] text-[#6A6E6E]">{caption}</div>
            </div>
          }
          onAccordionClick={() => {
            trackEvent({
              eventName: EventNames.UserClicked,
              payload: {
                ScreenName: analyticsScreen,
                eventId: PlansTimerEventIds.EducationAccordion,
                buttonLabel: title
              }
            })
            setOpenAccordions((previous) => ({
              ...previous,
              [title]: !previous[title]
            }))
          }}
        >
          <div className="pb-6 text-[13px] text-[#6A6E6E]">
            <RenderHTML innerHTMLText={content} />
          </div>
        </MFAccordion>
      ))}
    </div>
  )
}

/** EliteButton-style strip + overlapping CTA (INVERTED layout). */
export function TimerStackedCta({
  prompt,
  buttonLabel,
  promptClassName,
  onClick
}: {
  prompt: string
  buttonLabel: string
  promptClassName: string
  onClick: () => void
}) {
  return (
    <div className="flex w-full flex-col">
      <div className="relative z-0 h-[80px] rounded-[16px] bg-white">
        <div className="h-[75px]" />
        <div
          className={`absolute top-[8px] z-10 flex w-full items-center justify-center px-[14px] pb-8 
            text-center text-[13px] font-[500] leading-[150%] ${promptClassName}`}
        >
          {prompt}
        </div>
      </div>
      <PrimaryButton
        onClick={onClick}
        label={buttonLabel}
        aria-label={buttonLabel}
        primaryButtonClassName="relative z-10 mt-[-44px] !h-[56px] 
        !rounded-[16px] !text-[16px] !pb-[13px]"
      />
    </div>
  )
}

function NotifyBellIcon() {
  return (
    <svg
      className="h-4 w-4 shrink-0"
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden
    >
      <path
        d="M12.284 6.12556C12.284 4.98891 11.8325 3.89883 11.0288 3.0951C10.2251 2.29138 9.13497 1.83984 7.99833 1.83984C6.86168 1.83984 5.7716 2.29138 4.96787 3.0951C4.16414 3.89883 3.71261 4.98891 3.71261 6.12556V9.87556C3.71261 10.3018 3.54328 10.7106 3.24189 11.012C2.9405 11.3134 2.5317 11.4827 2.10547 11.4827H13.8912C13.465 11.4827 13.0561 11.3134 12.7547 11.012C12.4533 10.7106 12.284 10.3018 12.284 9.87556V6.12556Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.92578 14.1602H9.06864"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
